import { useCallback, useEffect, useRef, useState } from "react";

// Adds .in-view class when element scrolls into viewport (one-time)
export function useReveal(options = {}) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: options.threshold ?? 0.15, rootMargin: options.rootMargin ?? "0px 0px -40px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [options.threshold, options.rootMargin]);
  return ref;
}

// Observes all children with [data-reveal] inside a container
export function useRevealGroup() {
  const [container, setContainer] = useState(null);
  const ref = useCallback((node) => setContainer(node), []);
  useEffect(() => {
    if (!container) return;
    container.classList.add("reveal-ready");
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -30px 0px" }
    );
    const observeNewItems = () => {
      container.querySelectorAll("[data-reveal]:not(.in-view)").forEach((el) => obs.observe(el));
    };
    observeNewItems();
    const mutations = new MutationObserver(observeNewItems);
    mutations.observe(container, { childList: true, subtree: true });
    // Never leave page content invisible if a browser misses an intersection
    // notification during a client-side route transition.
    const fallback = window.setTimeout(() => {
      container.querySelectorAll("[data-reveal]").forEach((el) => el.classList.add("in-view"));
    }, 1200);
    return () => {
      window.clearTimeout(fallback);
      mutations.disconnect();
      obs.disconnect();
      container.classList.remove("reveal-ready");
    };
  }, [container]);
  return ref;
}

// Animated counter that starts when visible
export function useCounter(target, duration = 1800) {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started.current) {
            started.current = true;
            const start = performance.now();
            const tick = (now) => {
              const p = Math.min((now - start) / duration, 1);
              const eased = 1 - Math.pow(1 - p, 3);
              setValue(Math.round(eased * target));
              if (p < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [target, duration]);

  return [ref, value];
}
